import { useEffect } from 'react';
import { SaveIcon } from 'lucide-react';
import { FormProvider, useForm } from 'react-hook-form';

import { useUpdateReceiptItem } from '@/api/receipt/use-update-receipt-item.ts';
import type { ReceiptItemDto, ReceiptItemUpdateDto } from '@/api/types';
import FormInput from '@/components/form/form-input/form-input.tsx';
import { Button } from '@/components/ui/button';
import { formatPrice } from '@/helpers/formatters.ts';

type ReceiptItemRowProps = {
  item: ReceiptItemDto;
};

const ReceiptItemRow = ({ item }: ReceiptItemRowProps) => {
  const form = useForm<ReceiptItemUpdateDto>({
    defaultValues: { name: item.name, quantity: item.quantity },
  });

  useEffect(() => {
    form.reset({ name: item.name, quantity: item.quantity });
  }, [item.updatedAt]);

  const { mutateAsync: updateReceiptItem, isPending } = useUpdateReceiptItem(item.uuid);

  const onSubmit = ({ name, quantity }: ReceiptItemUpdateDto) => {
    updateReceiptItem({ name, quantity: Number(quantity) }).catch(() => undefined);
  };

  return (
    <FormProvider {...form}>
      <div className="border rounded-lg p-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <div className="sm:grow">
            <FormInput name="name" label="Name" labelHidden />
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="w-24">
              <FormInput name="quantity" label="Quantity" labelHidden type="number" />
            </div>
            <div className="flex grow flex-col items-end sm:w-24 sm:grow-0">
              <span className="text-lg font-semibold text-right">
                {formatPrice(item.amount + (item.discount || 0))}
              </span>
              {item.discount !== 0 && (
                <span className="text-xs text-red-600 text-right -mt-1">
                  {formatPrice(item.discount)}
                </span>
              )}
            </div>
            <div className="w-8">
              <Button
                disabled={!form.formState.isDirty || isPending}
                onClick={form.handleSubmit(onSubmit)}
                size="icon"
              >
                <SaveIcon />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </FormProvider>
  );
};

export default ReceiptItemRow;
